import React from 'react'
import classNames from 'classnames'
import { Media } from '@/payload-types'
import Author from '@/ui/atoms/Author'
import { Vendor } from '@/ui/atoms/Vendor'
import { Icon } from '@/ui/atoms/Icon'
import { UsedIn } from '@/ui/atoms/UsedIn'
import { Label, LabelProps } from '@/ui/atoms/Label'
import { CMSImage } from '@/ui/atoms/CMSImage'
import { AllowedFavouriteCollection } from '@/utils/favourites/types'

export type StandardCardProps = {
  title: string
  description?: string
  image?: Media | string | null
  imageTag?: string
  date?: Date | string | null
  minutesToRead?: number | null
  labels?: Array<{
    title: string
    variant?: LabelProps['variant']
  }>
  category?: {
    title: string
    colorScheme: string
  }
  company?: {
    name: string
    logo?: Media | string
  }
  authors?: Array<{
    name: string
    avatar?: Media | string | null
    verified?: boolean
  }>
  appearance?: 'filled' | 'outlined'
  isExternal?: boolean
  className?: string
  usedIn?: Array<{ process: string; wiki: string }>
  isAlsoUsedIn?: boolean
  productGroup?: { title: string; href: string }
  collection?: AllowedFavouriteCollection | null
  favItemId?: string
}

export const StandardCard: React.FC<StandardCardProps> = (props) => {
  const {
    title,
    description,
    image,
    imageTag,
    date,
    minutesToRead,
    labels,
    category,
    company,
    authors,
    usedIn,
    isAlsoUsedIn,
    productGroup,
    appearance = 'filled',
    isExternal,
    className: classNameFromProps,
  } = props

  return (
    <div
      className={classNames(
        'flex flex-col flex-1 rounded-md overflow-hidden transition-all duration-200 group',
        appearance === 'filled'
          ? 'bg-bg-subtle border border-border-subtle canhover:hover:bg-neutral-95'
          : 'bg-white border border-border-subtle canhover:hover:bg-bg-subtle',
        classNameFromProps,
      )}
    >
      {image && (
        <div className="relative w-full aspect-[2/1] overflow-hidden">
          <CMSImage
            resource={image}
            className="w-full h-full object-cover transition-transform duration-300 canhover:group-hover:scale-[102%]"
            fill
          />
          {imageTag && (
            <Label
              title={imageTag}
              variant="transparentInverted"
              size="small"
              className="absolute top-4 left-4"
            />
          )}
        </div>
      )}

      <div className="flex flex-col flex-1 p-4 relative">
        {/* Tags and Labels */}
        {((labels && labels.length > 0) || category) && (
          <div
            className={`flex items-center ${image ? 'mb-4' : 'mb-2'} gap-2 ${!labels ? 'justify-end' : 'justify-between'}`}
          >
            <div className="inline-flex flex-wrap gap-2 w-full">
              {category && (
                <Label title={category.title} colorScheme={category.colorScheme} size="small" />
              )}
              {labels?.map((label, index) => {
                return (
                  label &&
                  label.title && (
                    <Label
                      key={index}
                      title={label.title}
                      variant={label.variant || 'neutral'}
                      size="small"
                    />
                  )
                )
              })}
            </div>
          </div>
        )}
        {isExternal && (
          <Icon
            name="external-link-line"
            className="w-4 h-4 text-text-faded flex-shrink-0 absolute top-4 right-4"
          />
        )}
        {(date || minutesToRead) && (
          <div
            className={`flex items-center gap-2 text-label-XS-regular text-text-faded ${image ? 'mb-4' : 'mb-2'}`}
          >
            {date && <span>{new Date(date).toISOString().substring(0, 10)}</span>}
            {date && minutesToRead && (
              <div className="w-1 h-1 rounded-full bg-text-faded-inverted" />
            )}
            {minutesToRead && <span>{minutesToRead} min read</span>}
          </div>
        )}

        <h4 className={`text-title-S-medium text-text-base transition-colors w-[calc(100%-24px)]`}>
          {title}
        </h4>
        {company && (
          <Vendor
            name={company.name}
            logo={company.logo}
            size="extraMini"
            type="default"
            className={`mt-1`}
          />
        )}
        {description && (
          <p
            className={`text-body-XS-regular text-text-faded line-clamp-2 ${image ? 'mt-5' : 'mt-4'}`}
          >
            {description}
          </p>
        )}
        <UsedIn
          items={usedIn ?? []}
          isAlsoUsedIn={isAlsoUsedIn}
          productGroup={productGroup}
          showBorder={appearance === 'filled'}
          className="mt-auto pt-4"
        />
        {authors && authors.length > 0 && <Author authors={authors} size="small" />}
      </div>
    </div>
  )
}
